import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarPlus,
  Search,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Sun,
  Sunset,
  Check,
  ArrowLeft,
  Phone,
  Wrench,
  User,
  Clock,
  RefreshCw,
  AlertTriangle,
  PhoneCall,
  MessageSquare,
  CheckCircle,
  Calendar,
  MapPin,
} from 'lucide-react';
import clsx from 'clsx';
import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

const STEPS = [
  { label: 'Select Job', shortLabel: 'Job' },
  { label: 'Select Date', shortLabel: 'Date' },
  { label: 'Select Slot', shortLabel: 'Slot' },
  { label: 'Verify Details', shortLabel: 'Verify' },
  { label: 'Confirmed', shortLabel: 'Done' },
];

/** Horizontal step tracker */
function StepTracker({ currentStep, onStepClick }) {
  return (
    <div className="bg-white border-b border-brand-light/60 px-4 md:px-6 py-3">
      <div className="max-w-3xl mx-auto flex items-center">
        {STEPS.map((s, i) => {
          const isCompleted = currentStep > i;
          const isActive = currentStep === i;
          const isClickable = isCompleted && onStepClick;
          return (
            <div key={s.label} className="flex items-center flex-1 last:flex-0">
              <button
                onClick={() => isClickable && onStepClick(i)}
                disabled={!isClickable}
                className={clsx(
                  'flex items-center gap-2 transition-all',
                  isClickable && 'cursor-pointer hover:opacity-80',
                  !isClickable && 'cursor-default',
                )}
              >
                <div
                  className={clsx(
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                    isCompleted && 'bg-brand-primary text-white',
                    isActive && 'bg-brand-primary text-white ring-4 ring-brand-primary/15',
                    !isCompleted && !isActive && 'bg-gray-100 text-gray-400',
                  )}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span
                  className={clsx(
                    'text-xs font-medium hidden sm:block',
                    isActive && 'text-brand-primary',
                    isCompleted && 'text-brand-dark',
                    !isCompleted && !isActive && 'text-gray-400',
                  )}
                >
                  {s.label}
                </span>
                <span
                  className={clsx(
                    'text-xs font-medium sm:hidden',
                    isActive && 'text-brand-primary',
                    isCompleted && 'text-brand-dark',
                    !isCompleted && !isActive && 'text-gray-400',
                  )}
                >
                  {s.shortLabel}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <div className="flex-1 mx-3">
                  <div className="h-[2px] bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full bg-brand-primary rounded-full transition-all duration-500',
                        isCompleted ? 'w-full' : 'w-0',
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Simple month calendar component
function MonthCalendar({ selectedDate, onSelectDate }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  const { year, month } = viewMonth;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = new Date(year, month).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    setViewMonth((prev) => {
      const m = prev.month - 1;
      return m < 0
        ? { year: prev.year - 1, month: 11 }
        : { year: prev.year, month: m };
    });
  };

  const nextMonth = () => {
    setViewMonth((prev) => {
      const m = prev.month + 1;
      return m > 11
        ? { year: prev.year + 1, month: 0 }
        : { year: prev.year, month: m };
    });
  };

  // Build grid cells
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const formatDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Check if prev month button should be disabled (don't go before current month)
  const canGoPrev =
    year > today.getFullYear() ||
    (year === today.getFullYear() && month > today.getMonth());

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={prevMonth}
          disabled={!canGoPrev}
          className={clsx(
            'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
            canGoPrev
              ? 'hover:bg-brand-light text-brand-dark'
              : 'text-gray-200 cursor-not-allowed',
          )}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm font-bold text-brand-dark">{monthName}</span>
        <button
          onClick={nextMonth}
          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-brand-light text-brand-dark transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-bold text-brand-neutral uppercase tracking-wider py-1"
          >
            {d}
          </div>
        ))}

        {cells.map((date, i) => {
          if (!date)
            return <div key={`empty-${i}`} className="h-10" />;

          const dateStr = formatDateStr(date);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const isPast = date < today;
          const isToday = date.getTime() === today.getTime();
          const isSelected = selectedDate === dateStr;
          const isDisabled = isWeekend || isPast;

          return (
            <button
              key={dateStr}
              onClick={() => !isDisabled && onSelectDate(dateStr)}
              disabled={isDisabled}
              className={clsx(
                'h-10 rounded-lg text-sm font-medium transition-all',
                isSelected &&
                'bg-brand-primary text-white font-bold shadow-md shadow-brand-primary/20',
                isToday && !isSelected && 'ring-2 ring-brand-primary/30',
                isDisabled && 'text-gray-200 cursor-not-allowed',
                !isDisabled &&
                !isSelected &&
                'text-brand-dark hover:bg-brand-light cursor-pointer',
                isWeekend && 'bg-gray-50',
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function InternalBooking() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const { selectedLocationId, currentLocation } = useLocationContext();

  // Fetch live TBC jobs from Zuper
  const { data: liveData, isLoading: loadingReminders, refetch: refetchJobs } = useQuery({
    queryKey: ['live-jobs', weekOffset, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/live-jobs?weekOffset=${weekOffset}`);
      return data;
    },
  });

  // Map live data to match the format the rest of the component expects
  const campaignData = liveData ? { reminders: liveData.tbcJobs, campaign: { stats: liveData.counts } } : null;

  // Fetch date availability when a date is selected
  const { data: dateAvailability, isLoading: loadingAvailability } = useQuery({
    queryKey: ['date-availability', selectedDate, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(
        `/api/booking/date-availability/${selectedDate}`,
      );
      return data;
    },
    enabled: !!selectedDate && (step === 2 || step === 3),
  });

  // Confirm booking mutation
  const confirmMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(
        '/api/booking/internal-confirm',
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-booking-reminders'] });
      setStep(4);
    },
  });

  // TBC jobs from live Zuper data
  const tbcReminders = useMemo(() => {
    return campaignData?.reminders || [];
  }, [campaignData]);

  // Filter by search query
  const filteredReminders = useMemo(() => {
    if (!searchQuery.trim()) return tbcReminders;
    const q = searchQuery.toLowerCase();
    return tbcReminders.filter(
      (r) =>
        r.customer_name?.toLowerCase().includes(q) ||
        r.work_order_number?.toLowerCase().includes(q) ||
        r.job_category?.toLowerCase().includes(q),
    );
  }, [tbcReminders, searchQuery]);

  // Slot data from availability
  const slots = dateAvailability?.slots || [];

  const handleSelectJob = (reminder) => {
    setSelectedReminder(reminder);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(1);
  };

  const handleSelectDate = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleConfirm = () => {
    confirmMutation.mutate({
      customerId: selectedReminder.customer_uid,
      jobId: selectedReminder.job_uid,
      selectedDate,
      selectedTime: selectedSlot.start,
    });
  };

  const handleBack = () => {
    if (step === 0) return;
    if (step === 4) { handleStartOver(); return; }
    if (step === 3) { setSelectedSlot(null); setStep(2); return; }
    if (step === 2) { setSelectedDate(null); setSelectedSlot(null); setStep(1); return; }
    setStep(0);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleStartOver = () => {
    setStep(0);
    setSelectedReminder(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSearchQuery('');
    confirmMutation.reset();
  };

  const handleStepClick = (targetStep) => {
    if (targetStep >= step) return; // Can only go back
    if (targetStep === 0) { setSelectedReminder(null); setSelectedDate(null); setSelectedSlot(null); setSearchQuery(''); }
    if (targetStep === 1) { setSelectedDate(null); setSelectedSlot(null); }
    if (targetStep === 2) { setSelectedSlot(null); }
    setStep(targetStep);
  };

  // Format 24h time to 12h (e.g. "14:00" -> "2:00 PM")
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const dateRange = (() => {
    const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((day + 6) % 7) + weekOffset * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${fmt(monday)} - ${fmt(sunday)}`;
  })();

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'Current Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset > 0) return `${weekOffset} Weeks Ahead`;
    return `${Math.abs(weekOffset)} Weeks Ago`;
  };

  const goToPrevWeek = () => setWeekOffset(w => w - 1);
  const goToNextWeek = () => setWeekOffset(w => w + 1);
  const goToCurrentWeek = () => setWeekOffset(0);

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={loadingReminders} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <CalendarPlus className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Book for Customer</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Schedule service appointments on behalf of customers</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          {selectedReminder && step >= 1 && step <= 3 && (
            <div className="text-right">
              <p className="text-xs text-brand-neutral">Booking for</p>
              <p className="text-sm font-medium text-brand-dark">
                {selectedReminder.customer_name}
              </p>
            </div>
          )}
          <button
            onClick={() => refetchJobs()}
            disabled={loadingReminders}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingReminders ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loadingReminders ? 'Loading...' : 'Sync'}</span>
          </button>
        </div>
      </header>

      {/* Step Tracker */}
      <StepTracker currentStep={step} onStepClick={handleStepClick} />

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Step 0: Select Job */}
          {step === 0 && (
            <>
            {/* Week Navigation */}
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light p-3 md:p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPrevWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center flex-1">
                  <h2 className="text-sm md:text-base font-bold text-brand-dark">{getWeekLabel()}</h2>
                  <p className="text-xs text-brand-neutral">{dateRange}</p>
                </div>
                <button
                  onClick={goToNextWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              {weekOffset !== 0 && (
                <button
                  onClick={goToCurrentWeek}
                  className="w-full mt-2 px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
                >
                  Back to Current Week
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">
                    Select a TBC Job
                  </h3>
                  <p className="text-xs text-brand-neutral mt-0.5">
                    Choose a job from the current campaign to book
                  </p>
                </div>
                {!loadingReminders && (
                  <span className="text-xs font-medium text-brand-neutral bg-brand-light px-2.5 py-1 rounded-full">
                    {filteredReminders.length} job{filteredReminders.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="p-4 md:p-6">
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by customer name or work order..."
                    className="w-full pl-10 pr-4 py-2.5 border border-brand-light rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                  />
                </div>

                {loadingReminders ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <Loader2 className="w-7 h-7 animate-spin text-brand-primary mb-2" />
                    <p className="text-xs text-brand-neutral">Loading jobs...</p>
                  </div>
                ) : filteredReminders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <div className="w-14 h-14 rounded-full bg-brand-light/50 flex items-center justify-center mb-3">
                      <CalendarPlus className="w-7 h-7 text-brand-neutral/40" />
                    </div>
                    <p className="text-sm font-medium text-brand-dark mb-1">
                      {searchQuery ? 'No matching jobs' : 'No TBC jobs available'}
                    </p>
                    <p className="text-xs text-brand-neutral">
                      {searchQuery ? 'Try a different search term' : 'Sync from Zuper to load new jobs'}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => refetchJobs()}
                        className="mt-4 px-4 py-2 text-xs font-medium text-brand-primary bg-brand-light rounded-xl hover:bg-brand-primary hover:text-white transition-colors"
                      >
                        Sync from Zuper
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
                    {filteredReminders.map((reminder) => {
                      const needsCall = reminder.reminder_count >= 3;
                      const statusConfig = {
                        pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
                        sent: { label: 'Sent', color: 'bg-blue-50 text-blue-600' },
                        failed: { label: 'Failed', color: 'bg-red-50 text-red-600' },
                      };
                      const st = statusConfig[reminder.status] || statusConfig.pending;

                      return (
                        <button
                          key={reminder.id}
                          onClick={() => handleSelectJob(reminder)}
                          className="w-full text-left px-4 py-3.5 rounded-xl border border-brand-light/80 hover:border-brand-primary/60 hover:bg-brand-light/20 transition-all group"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-light to-white flex items-center justify-center flex-shrink-0 shadow-sm">
                                <User className="w-4 h-4 text-brand-primary" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-brand-dark truncate">
                                  {reminder.customer_name}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  {reminder.work_order_number && (
                                    <span className="text-[11px] font-mono text-brand-neutral">
                                      {reminder.work_order_number}
                                    </span>
                                  )}
                                  {reminder.job_category && (
                                    <>
                                      <span className="text-brand-neutral/40">·</span>
                                      <span className="text-[11px] text-brand-neutral">{reminder.job_category}</span>
                                    </>
                                  )}
                                </div>
                                {/* Asset info */}
                                {reminder.asset_name && (
                                  <p className="text-[11px] text-brand-neutral/70 mt-0.5 truncate">
                                    {reminder.asset_name}
                                    {reminder.asset_serial_number && (
                                      <span className="font-mono ml-1 opacity-70">SN: {reminder.asset_serial_number}</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-brand-neutral/40 group-hover:text-brand-primary transition-colors flex-shrink-0" />
                          </div>
                          {/* Bottom row: date + phone + badges */}
                          <div className="flex items-center gap-2 mt-2 ml-12 flex-wrap">
                            {reminder.scheduled_date && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-brand-neutral">
                                <Calendar className="w-3 h-3" />
                                {reminder.scheduled_date}
                              </span>
                            )}
                            {reminder.customer_phone && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-brand-neutral">
                                <Phone className="w-3 h-3" />
                                {reminder.customer_phone}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                              TBC
                            </span>
                            {reminder.workshop_location && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-brand-neutral bg-brand-light/60 px-1.5 py-0.5 rounded">
                                <MapPin className="w-3 h-3" />
                                {reminder.workshop_location}
                              </span>
                            )}
                            {reminder.reminder_count > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-brand-neutral">
                                <MessageSquare className="w-3 h-3" />
                                {reminder.reminder_count}x
                              </span>
                            )}
                            {needsCall && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600">
                                <PhoneCall className="w-3 h-3" />
                                Needs Call
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* Step 1: Calendar + Slots + Mechanic (progressive disclosure) */}
          {/* Step 1: Select Date */}
          {step === 1 && (
            <>
              {/* Customer info banner */}
              <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-4 text-white shadow-md">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/70 text-[11px] font-medium uppercase tracking-wider">
                      {selectedReminder?.job_category || 'Service'}
                    </p>
                    <p className="font-bold text-lg leading-tight mt-0.5">
                      {selectedReminder?.customer_name}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {selectedReminder?.work_order_number && (
                        <span className="text-white/90 text-xs font-mono bg-white/10 px-2 py-0.5 rounded">
                          WO: {selectedReminder.work_order_number}
                        </span>
                      )}
                      {selectedReminder?.customer_phone && (
                        <span className="inline-flex items-center gap-1 text-white/80 text-xs">
                          <Phone className="w-3 h-3" />
                          {selectedReminder.customer_phone}
                        </span>
                      )}
                    </div>
                    {selectedReminder?.asset_name && (
                      <p className="text-white/70 text-xs mt-1">
                        {selectedReminder.asset_name}
                        {selectedReminder.asset_serial_number && (
                          <span className="ml-1 opacity-80">· SN: {selectedReminder.asset_serial_number}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Calendar */}
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light bg-brand-light/30">
                  <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4 text-brand-primary" />
                    Select Date
                  </h3>
                  <p className="text-xs text-brand-neutral mt-0.5">
                    Choose a weekday for the service
                  </p>
                </div>
                <div className="p-4 md:p-6">
                  <MonthCalendar
                    selectedDate={selectedDate}
                    onSelectDate={handleSelectDate}
                  />
                </div>
              </div>
            </>
          )}

          {/* Step 2: Select Time Slot */}
          {step === 2 && (
            <>
              <div className="text-center text-sm text-brand-neutral">
                <span className="font-medium text-brand-dark">{formatDate(selectedDate)}</span>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                  <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                    <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-primary" />
                      Select Time Slot
                    </h3>
                    <p className="text-xs text-brand-neutral mt-0.5">
                      {formatDate(selectedDate)}
                    </p>
                  </div>
                  <div className="p-4 md:p-6">
                    {loadingAvailability ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-brand-primary mb-2" />
                        <p className="text-xs text-brand-neutral">Checking availability...</p>
                      </div>
                    ) : dateAvailability?.isWeekend ? (
                      <p className="text-sm text-brand-neutral text-center py-4">
                        No bookings available on weekends
                      </p>
                    ) : slots.length === 0 ? (
                      <p className="text-sm text-brand-neutral text-center py-4">
                        No time slots configured
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {slots.map((slot) => {
                          const isMorning = slot.label?.toLowerCase().includes('morning');
                          const Icon = isMorning ? Sun : Sunset;
                          const isSelected = selectedSlot?.label === slot.label;
                          const capacityPct = slot.capacity > 0 ? (slot.booked / slot.capacity) * 100 : 0;

                          return (
                            <button
                              key={slot.label}
                              onClick={() => slot.available && handleSelectSlot(slot)}
                              disabled={!slot.available}
                              className={clsx(
                                'w-full flex items-center gap-4 px-4 py-4 rounded-xl border-2 transition-all text-left',
                                isSelected
                                  ? 'border-brand-primary bg-brand-light/50 shadow-md shadow-brand-primary/10'
                                  : slot.available
                                    ? 'border-brand-light hover:border-brand-primary/50 hover:shadow-sm'
                                    : 'border-red-100 bg-red-50/30 cursor-not-allowed',
                              )}
                            >
                              <div
                                className={clsx(
                                  'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                                  isSelected
                                    ? 'bg-brand-primary/10'
                                    : slot.available
                                      ? isMorning ? 'bg-amber-50' : 'bg-orange-50'
                                      : 'bg-red-50',
                                )}
                              >
                                <Icon
                                  className={clsx(
                                    'w-6 h-6',
                                    isSelected
                                      ? 'text-brand-primary'
                                      : slot.available
                                        ? isMorning ? 'text-amber-500' : 'text-orange-500'
                                        : 'text-red-300',
                                  )}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p
                                  className={clsx(
                                    'text-sm font-bold',
                                    isSelected ? 'text-brand-primary' : slot.available ? 'text-brand-dark' : 'text-gray-400 line-through',
                                  )}
                                >
                                  {slot.label}
                                </p>
                                <p className={clsx('text-xs mt-0.5', slot.available ? 'text-brand-neutral' : 'text-gray-400')}>
                                  {formatTime(slot.start)} – {formatTime(slot.end)}
                                </p>
                                {slot.description && (
                                  <p className="text-[10px] text-brand-neutral/60 mt-0.5">{slot.description}</p>
                                )}
                                {/* Availability bar */}
                                <div className="mt-2 flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className={clsx(
                                        'h-full rounded-full transition-all',
                                        capacityPct >= 100 ? 'bg-red-400' : capacityPct >= 75 ? 'bg-amber-400' : 'bg-brand-primary',
                                      )}
                                      style={{ width: `${Math.min(capacityPct, 100)}%` }}
                                    />
                                  </div>
                                  <span className={clsx('text-[10px] font-semibold', slot.available ? 'text-brand-neutral' : 'text-red-500')}>
                                    {slot.booked}/{slot.capacity}
                                  </span>
                                </div>
                              </div>
                              {isSelected && (
                                <Check className="w-5 h-5 text-brand-primary flex-shrink-0" />
                              )}
                              {!slot.available && !isSelected && (
                                <span className="text-[10px] font-semibold text-red-500 px-2 py-0.5 bg-red-50 rounded-full flex-shrink-0">Full</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
            </>
          )}

          {/* Step 3: Verify Booking Details */}
          {step === 3 && (
            <>
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                  <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                    <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-brand-primary" />
                      Confirm Booking
                    </h3>
                  </div>
                  <div className="p-4 md:p-6 space-y-4">
                    {/* Receipt-style summary */}
                    <div className="space-y-0 divide-y divide-brand-light/60">
                      <div className="flex items-center gap-3 py-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-light/50 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-brand-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-brand-neutral uppercase tracking-wider">Customer</p>
                          <p className="text-sm font-semibold text-brand-dark">{selectedReminder?.customer_name}</p>
                        </div>
                        {selectedReminder?.work_order_number && (
                          <span className="text-xs font-mono text-brand-neutral bg-brand-light px-2 py-0.5 rounded">
                            {selectedReminder.work_order_number}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 py-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-light/50 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-4 h-4 text-brand-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[10px] text-brand-neutral uppercase tracking-wider">Date & Time</p>
                          <p className="text-sm font-semibold text-brand-dark">{formatDate(selectedDate)}</p>
                          <p className="text-xs text-brand-neutral">
                            {selectedSlot?.label} ({formatTime(selectedSlot?.start)} – {formatTime(selectedSlot?.end)})
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 py-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-light/50 flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-4 h-4 text-brand-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[10px] text-brand-neutral uppercase tracking-wider">Mechanic</p>
                          <p className="text-sm font-semibold text-brand-dark">Auto-assigned by system</p>
                        </div>
                      </div>
                      {selectedReminder?.customer_phone && (
                        <div className="flex items-center gap-3 py-3">
                          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] text-brand-neutral uppercase tracking-wider">WhatsApp Confirmation</p>
                            <p className="text-xs text-green-600 font-medium">
                              Will be sent to {selectedReminder.customer_phone}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {confirmMutation.isError && (
                      <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600">
                          {confirmMutation.error?.response?.data?.error ||
                            'Failed to confirm booking. Please try again.'}
                        </p>
                      </div>
                    )}

                    <button
                      onClick={handleConfirm}
                      disabled={confirmMutation.isPending}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg hover:scale-[1.01] transition-all shadow-sm disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {confirmMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Confirming...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Confirm Booking
                        </>
                      )}
                    </button>
                  </div>
                </div>
            </>
          )}

          {/* Step 4: Confirmed */}
          {step === 4 && (
            <div className="bg-white rounded-2xl shadow-md border border-brand-light/80 overflow-hidden">
              <div className="p-6 md:p-10 text-center">
                {/* Animated checkmark */}
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center mx-auto mb-5 animate-[scale-in_0.4s_ease-out]">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-1">
                  Booking Confirmed!
                </h3>
                <p className="text-sm text-brand-neutral mb-6">
                  The service has been scheduled successfully.
                </p>

                {/* Booking details card */}
                <div className="bg-brand-light/30 rounded-xl p-4 max-w-sm mx-auto text-left space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-brand-neutral">Customer</span>
                    <span className="text-sm font-semibold text-brand-dark">
                      {selectedReminder?.customer_name}
                    </span>
                  </div>
                  {selectedReminder?.work_order_number && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-brand-neutral">Work Order</span>
                      <span className="text-sm font-mono text-brand-dark">
                        {selectedReminder.work_order_number}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-brand-neutral">Date</span>
                    <span className="text-sm font-semibold text-brand-dark">
                      {formatDate(selectedDate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-brand-neutral">Time Slot</span>
                    <span className="text-sm text-brand-dark">
                      {selectedSlot?.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-brand-neutral">Mechanic</span>
                    <span className="text-sm text-brand-dark">Auto-assigned</span>
                  </div>
                  {selectedReminder?.customer_phone && (
                    <div className="flex items-center justify-between pt-2 border-t border-brand-light">
                      <span className="text-xs text-brand-neutral">WhatsApp</span>
                      {confirmMutation.data?.whatsappStatus === 'sent' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          Confirmation sent
                        </span>
                      ) : confirmMutation.data?.whatsappStatus?.startsWith('FAILED') ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500" title={confirmMutation.data.whatsappStatus}>
                          <AlertTriangle className="w-3 h-3" />
                          Failed — check template name in Settings
                        </span>
                      ) : (
                        <span className="text-xs text-brand-neutral">—</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center gap-3 mt-8">
                  <button
                    onClick={handleStartOver}
                    className="flex-1 max-w-[160px] px-5 py-3 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all shadow-sm"
                  >
                    Book Another
                  </button>
                  <button
                    onClick={() => navigate('/booking-alerts')}
                    className="flex-1 max-w-[160px] px-5 py-3 text-sm font-medium text-brand-dark bg-brand-light hover:bg-brand-light/70 rounded-xl transition-all"
                  >
                    View Reminders
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
}
